import { formatAccountDisplayName } from '../../utils/scanReportUtils';

/*
 * Filtering helpers for the Quality & Completeness report.
 *
 * Pure functions, deliberately free of React: the report, the coverage
 * heatmap and the PDF export all narrow the same payload, and this is what
 * stops them drifting apart. On a forensic report a filtered view that
 * disagrees with its own export is worse than no filter at all.
 *
 * The two filters are different in kind and are applied differently:
 *   - account type filters ROWS (which accounts appear at all)
 *   - year filters COLUMNS (which months the axis spans) and, by overlap,
 *     which statements are listed
 * A statement running Dec 28 - Jan 27 belongs to BOTH years, so year
 * membership is an overlap test, never an equality test on one date.
 */

const YEAR_RE = /^(\d{4})-(\d{2})/;

const yearOf = (dateStr) => {
    if (typeof dateStr !== 'string') return null;
    const match = YEAR_RE.exec(dateStr);
    return match ? match[1] : null;
};

// Every calendar year a statement touches, inclusive of both endpoints.
// Missing-statement placeholders carry expected_* dates instead.
export const statementYears = (statement) => {
    if (!statement) return [];
    const begin = yearOf(statement.begin_date) || yearOf(statement.expected_begin_date);
    const end = yearOf(statement.end_date) || yearOf(statement.expected_end_date);

    if (!begin && !end) return [];
    if (!begin) return [end];
    if (!end) return [begin];

    const from = Math.min(Number(begin), Number(end));
    const to = Math.max(Number(begin), Number(end));
    const years = [];
    for (let y = from; y <= to; y += 1) years.push(String(y));
    return years;
};

// Sorted, de-duplicated list of every year present anywhere in the payload.
export const collectYears = (data) => {
    if (!data) return [];
    const years = new Set();
    Object.values(data).forEach((account) => {
        (account.combined_statements || []).forEach((statement) => {
            statementYears(statement).forEach((y) => years.add(y));
        });
    });
    return [...years].sort();
};

/*
 * One row per account. The report payload is an object keyed by account_id;
 * this flattens it so the filter bar can reason about accounts as a list,
 * with `accountId` carried through to map the result back.
 */
export const buildAccountRows = (data) => {
    if (!data) return [];
    return Object.keys(data)
        .sort((a, b) => (data[a].sort_index ?? 0) - (data[b].sort_index ?? 0))
        .map((accountId) => ({
            accountId,
            accountGroup: data[accountId].account_group || 'Other',
            accountLabel: formatAccountDisplayName(data[accountId].bank_account) || `Account ${accountId}`,
        }));
};

// Every account group present in the payload, in the backend's canonical
// order (sort_index already encodes group rank).
export const collectAccountGroups = (data) => {
    if (!data) return [];
    const seen = [];
    Object.keys(data)
        .sort((a, b) => (data[a].sort_index ?? 0) - (data[b].sort_index ?? 0))
        .forEach((accountId) => {
            const group = data[accountId].account_group || 'Other';
            if (!seen.includes(group)) seen.push(group);
        });
    return seen;
};

export const describeAccountGroupFilter = (selectedGroups, allGroups) => {
    if (!selectedGroups || !allGroups || allGroups.length === 0) return [];
    if (selectedGroups.length === allGroups.length) return [];
    if (selectedGroups.length === 0) return ['Account Type: none selected'];
    return [`Account Type: ${[...selectedGroups].join(', ')}`];
};

const statementInYears = (statement, yearSet) => {
    if (!yearSet) return true;
    const years = statementYears(statement);
    // A statement with no usable dates is kept: dropping it would silently
    // hide a record whose period we simply failed to read.
    if (years.length === 0) return true;
    return years.some((y) => yearSet.has(y));
};

/*
 * Narrow the payload to the selected accounts and years.
 *
 * `visibleAccountIds` — null means "all accounts" (no account filter active).
 * `visibleYears`      — null means "all years".
 */
export const applyQualityFilters = (data, {
    visibleAccountIds = null,
    visibleYears = null,
} = {}) => {
    if (!data) return {};

    const accountSet = visibleAccountIds ? new Set(visibleAccountIds) : null;
    const yearSet = visibleYears ? new Set(visibleYears) : null;

    const result = {};
    Object.keys(data).forEach((accountId) => {
        if (accountSet && !accountSet.has(accountId)) return;

        const account = data[accountId];
        const statements = (account.combined_statements || [])
            .filter((statement) => statementInYears(statement, yearSet));

        result[accountId] = { ...account, combined_statements: statements };
    });

    return result;
};

// Human-readable lines for the PDF's "Filters Applied:" block. Returns [] when a control is in its no-op state,
// so an unfiltered export carries no misleading filter stamp.
export const describeYearFilter = (selectedYears, allYears) => {
    if (!selectedYears || !allYears || allYears.length === 0) return [];
    if (selectedYears.length === allYears.length) return [];
    if (selectedYears.length === 0) return ['Years: none selected'];

    const sorted = [...selectedYears].sort();
    if (sorted.length === 1) return [`Years: ${sorted[0]}`];

    // The year control is a from/to range, so the selection is the run of
    // years that exist between two endpoints — describe it as a span rather
    // than listing every year, which would grow without bound on a long case.
    // A gap year (a case with nothing in 2022) is a hole in the data, not a
    // hole in the selection, so it does not break the span.
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const isRun = allYears
        .filter((y) => y >= first && y <= last)
        .join(',') === sorted.join(',');

    return [isRun ? `Years: ${first} to ${last}` : `Years: ${sorted.join(', ')}`];
};
